import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["count"];
import { useState, useMemo } from 'react';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Button } from 'foundations-components/transitional/components/Button';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { TextArea } from 'foundations-components/transitional/components/TextArea';
import { CharacterCounter } from 'foundations-components/transitional/components/CharacterCounter';
import { Select } from 'foundations-components/transitional/components/Select';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { useAdmins } from '../../../hooks/useAdmins';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DETAILS_MAX_LENGTH = 1000;
export function RequestAccessModal({
  // This is temporary, will incrementally build on this and this will be used.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  requestAccessType,
  appLocationLabel,
  onRequestAccess,
  onClose
}) {
  const [selectedAdminValue, setSelectedAdminValue] = useState('');
  const [additionalDetails, setAdditionalDetails] = useState('');
  const {
    admins,
    isLoading: isLoadingAdmins,
    hasError: hasAdminsError
  } = useAdmins();
  const adminOptions = useMemo(() => admins.map(admin => ({
    text: admin.name,
    value: admin.userId
  })), [admins]);
  const isSelectedAdminStillValid = admins.some(admin => admin.userId === selectedAdminValue);
  const effectiveSelectedAdminValue = isSelectedAdminStillValid ? selectedAdminValue : '';
  function handleRequestAccess() {
    onRequestAccess([effectiveSelectedAdminValue], additionalDetails);
  }
  function getDisabledTooltip() {
    if (isLoadingAdmins) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "access-check-lib.requestAccessModal.disabledTooltip.loadingAdmins"
      });
    }
    if (hasAdminsError) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "access-check-lib.requestAccessModal.disabledTooltip.adminsError"
      });
    }
    if (admins.length === 0) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "access-check-lib.requestAccessModal.disabledTooltip.noAdmins"
      });
    }
    const missingFields = [];
    if (!effectiveSelectedAdminValue) {
      missingFields.push({
        id: 'admin',
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "access-check-lib.requestAccessModal.disabledTooltip.missingAdmin"
        })
      });
    }
    if (additionalDetails.trim().length === 0) {
      missingFields.push({
        id: 'details',
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "access-check-lib.requestAccessModal.disabledTooltip.missingDetails"
        })
      });
    }
    if (additionalDetails.trim().length > 0 && additionalDetails.length > DETAILS_MAX_LENGTH) {
      missingFields.push({
        id: 'characterLimit',
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "access-check-lib.requestAccessModal.disabledTooltip.characterLimitExceeded"
        })
      });
    }
    if (missingFields.length === 0) {
      return undefined;
    }
    if (missingFields.length === 1) {
      return missingFields[0].message;
    }
    return /*#__PURE__*/_jsx(TFlex, {
      flexDirection: "column",
      gap: 4,
      children: missingFields.map(({
        id,
        message
      }) => /*#__PURE__*/_jsx(View, {
        children: message
      }, id))
    });
  }
  const disabledTooltip = getDisabledTooltip();
  const isSubmitDisabled = disabledTooltip !== undefined;
  return /*#__PURE__*/_jsxs(Modal, {
    use: "default",
    width: 634,
    onEsc: onClose,
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: appLocationLabel ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "access-check-lib.requestAccessModal.title",
          options: {
            appLocationLabel
          }
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "access-check-lib.requestAccessModal.titleNoLocation"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsxs(TFlex, {
        flexDirection: "column",
        align: "stretch",
        gap: 16,
        children: [/*#__PURE__*/_jsx(Text, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "access-check-lib.requestAccessModal.description"
          })
        }), /*#__PURE__*/_jsx(FormControl, {
          label: I18n.text('access-check-lib.requestAccessModal.adminDropdownLabel'),
          required: true,
          children: /*#__PURE__*/_jsx(Select, {
            value: effectiveSelectedAdminValue,
            onChange: evt => setSelectedAdminValue(evt.target.value),
            options: adminOptions,
            disabled: isLoadingAdmins || hasAdminsError,
            isLoading: isLoadingAdmins,
            inlineSize: "100%",
            placeholder: isLoadingAdmins ? I18n.text('access-check-lib.requestAccessModal.loadingAdmins') : I18n.text('access-check-lib.requestAccessModal.selectAnAdminPlaceholder')
          })
        }), hasAdminsError && /*#__PURE__*/_jsx(Alert, {
          type: "danger",
          inlineSize: "100%",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "access-check-lib.requestAccessModal.loadAdminsError"
          })
        }), /*#__PURE__*/_jsx(CharacterCounter, {
          id: "request-access-details",
          counter: "down",
          value: additionalDetails,
          max: DETAILS_MAX_LENGTH,
          onChange: evt => setAdditionalDetails(evt.target.value),
          renderInput: _ref => {
            let {
                count
              } = _ref,
              counterProps = _objectWithoutPropertiesLoose(_ref, _excluded);
            return /*#__PURE__*/_jsx(FormControl, {
              label: I18n.text('access-check-lib.requestAccessModal.detailsLabel'),
              required: true,
              characterCount: count,
              error: count < 0,
              children: /*#__PURE__*/_jsx(TextArea, Object.assign({}, counterProps, {
                placeholder: I18n.text('access-check-lib.requestAccessModal.detailsPlaceholder'),
                inlineSize: "100%"
              }))
            });
          }
        })]
      })
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(Tooltip, {
        title: disabledTooltip,
        disabled: !isSubmitDisabled,
        children: /*#__PURE__*/_jsx(Button, {
          use: "primary",
          onClick: handleRequestAccess,
          disabled: isSubmitDisabled,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "access-check-lib.requestAccessModal.requestAccessButton"
          })
        })
      }), /*#__PURE__*/_jsx(Button, {
        use: "secondary",
        onClick: onClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "access-check-lib.requestAccessModal.cancelButton"
        })
      })]
    })]
  });
}